/* ============================================================
   MILLER & SON — MILLER-THEME.JS
   Scroll Animations · Parallax · Utilities · Product Tabs
   ============================================================ */

document.addEventListener('DOMContentLoaded', () => {
  // Wait a moment for Shopify's dynamic content to load
  setTimeout(() => {
    initScrollReveal();
    initParallax();
    initSmokeCanvas();
    initCountUp();
    initStickyNav();
    initProductTabs();
  }, 100);
});

/* ─── SCROLL REVEAL ─────────────────────────────────────────── */
function initScrollReveal() {
  const targets = document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');
  if (!targets.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

  targets.forEach(el => observer.observe(el));
}

/* ─── PARALLAX LAYERS ───────────────────────────────────────── */
function initParallax() {
  const layers = document.querySelectorAll('[data-parallax]');
  if (!layers.length) return;

  let ticking = false;
  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(() => {
        const scrollY = window.scrollY;
        layers.forEach(layer => {
          const speed = parseFloat(layer.dataset.parallax) || 0.3;
          const rect = layer.closest('section')?.getBoundingClientRect();
          if (rect && rect.bottom > 0 && rect.top < window.innerHeight) {
            const offset = scrollY * speed;
            layer.style.transform = `translateY(${offset}px)`;
          }
        });
        ticking = false;
      });
      ticking = true;
    }
  });

  // Mouse parallax for hero
  const hero = document.querySelector('.hero-parallax');
  if (hero) {
    hero.addEventListener('mousemove', (e) => {
      const rect = hero.getBoundingClientRect();
      const cx = (e.clientX - rect.left) / rect.width - 0.5;
      const cy = (e.clientY - rect.top) / rect.height - 0.5;

      hero.querySelectorAll('[data-mouse-parallax]').forEach(el => {
        const depth = parseFloat(el.dataset.mouseParallax) || 1;
        el.style.transform = `translate(${cx * depth * 20}px, ${cy * depth * 10}px)`;
      });
    });
  }
}

/* ─── SMOKE CANVAS ──────────────────────────────────────────── */
function initSmokeCanvas() {
  const canvas = document.getElementById('smoke-canvas');
  if (!canvas) return;

  const ctx = canvas.getContext('2d');
  let particles = [];

  function resize() {
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
  }
  resize();
  window.addEventListener('resize', resize);

  class Particle {
    constructor() { this.reset(); }
    reset() {
      this.x = Math.random() * canvas.width;
      this.y = canvas.height + 20;
      this.size = Math.random() * 60 + 20;
      this.speedY = Math.random() * 0.5 + 0.2;
      this.speedX = (Math.random() - 0.5) * 0.3;
      this.opacity = Math.random() * 0.06 + 0.02;
      this.life = 0;
      this.maxLife = Math.random() * 200 + 100;
    }
    update() {
      this.y -= this.speedY;
      this.x += this.speedX;
      this.size += 0.2;
      this.life++;
      if (this.life > this.maxLife || this.y < -this.size) this.reset();
    }
    draw() {
      const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.size);
      const alpha = this.opacity * (1 - this.life / this.maxLife);
      gradient.addColorStop(0, `rgba(255,255,255,${alpha})`);
      gradient.addColorStop(1, `rgba(255,255,255,0)`);
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
      ctx.fillStyle = gradient;
      ctx.fill();
    }
  }

  for (let i = 0; i < 18; i++) {
    const p = new Particle();
    p.life = Math.random() * p.maxLife; // stagger starts
    particles.push(p);
  }

  function animate() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    particles.forEach(p => { p.update(); p.draw(); });
    requestAnimationFrame(animate);
  }
  animate();
}

/* ─── COUNT UP ANIMATION ────────────────────────────────────── */
function initCountUp() {
  const counters = document.querySelectorAll('[data-count]');
  if (!counters.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      const target = parseFloat(el.dataset.count);
      const suffix = el.dataset.suffix || '';
      const prefix = el.dataset.prefix || '';
      const duration = 1600;
      const start = performance.now();

      function tick(now) {
        const elapsed = now - start;
        const progress = Math.min(elapsed / duration, 1);
        const ease = 1 - Math.pow(1 - progress, 3);
        const value = Math.round(ease * target);
        el.textContent = prefix + value + suffix;
        if (progress < 1) requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
      observer.unobserve(el);
    });
  }, { threshold: 0.5 });

  counters.forEach(el => observer.observe(el));
}

/* ─── STICKY NAV ────────────────────────────────────────────── */
function initStickyNav() {
  const nav = document.querySelector('.site-nav');
  if (!nav) return;

  let lastScroll = 0;
  window.addEventListener('scroll', () => {
    const scrollY = window.scrollY;
    nav.classList.toggle('nav-scrolled', scrollY > 60);
    nav.classList.toggle('nav-hidden', scrollY > lastScroll + 5 && scrollY > 200);
    nav.classList.toggle('nav-visible', scrollY < lastScroll);
    lastScroll = scrollY;
  });

  // Mobile menu toggle - Check if these elements exist in your theme
  const toggle = document.querySelector('.nav-toggle');
  const menu = document.querySelector('.nav-menu');
  if (toggle && menu) {
    toggle.addEventListener('click', () => {
      const open = menu.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open);
      document.body.style.overflow = open ? 'hidden' : '';
    });
  }
}

/* ─── PRODUCT TAB SELECTOR ──────────────────────────────────── */
function initProductTabs() {
  const tabs = document.querySelectorAll('.product-tab');
  const panels = document.querySelectorAll('.product-panel');

  if (!tabs.length || !panels.length) return;

  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      const target = tab.dataset.tab;
      tabs.forEach(t => t.classList.remove('active'));
      panels.forEach(p => p.classList.remove('active'));
      tab.classList.add('active');
      const targetPanel = document.querySelector(`.product-panel[data-panel="${target}"]`);
      if (targetPanel) {
        targetPanel.classList.add('active');
      }
    });
  });

  // Activate first tab by default if none active
  const hasActiveTab = Array.from(tabs).some(t => t.classList.contains('active'));
  if (!hasActiveTab && tabs.length > 0) {
    tabs[0].click();
  }
}

/* ─── EXPOSE FUNCTIONS GLOBALLY ─────────────────────────────── */
window.initMillerTheme = {
  initScrollReveal,
  initParallax,
  initSmokeCanvas,
  initCountUp,
  initStickyNav,
  initProductTabs
};






















/* ─── PRODUCT CARD IMAGE ASPECT RATIO ──────────────────────── */
function initProductCardAspectRatio() {
  const productCards = document.querySelectorAll('.product-card');
  
  productCards.forEach(card => {
    // Find the image wrapper
    const wrapper = card.querySelector('.card-gallery, .product__media-wrapper');
    const images = card.querySelectorAll('img');
    
    if (wrapper) {
      // Get the aspect ratio setting from the block
      const block = card.closest('[data-block-id]');
      let ratio = 'square'; // default
      
      // Try to get the ratio from the block settings
      if (block) {
        const ratioAttr = block.dataset.imageRatio;
        if (ratioAttr) ratio = ratioAttr;
      }
      
      // Apply the appropriate aspect ratio
      wrapper.classList.add(`card-gallery--${ratio}`);
      
      // Set padding bottom based on ratio
      switch(ratio) {
        case 'portrait':
          wrapper.style.paddingBottom = '133.33%';
          break;
        case 'landscape':
          wrapper.style.paddingBottom = '75%';
          break;
        case 'adapt':
          // Will be handled by the image load event
          break;
        case 'square':
        default:
          wrapper.style.paddingBottom = '100%';
      }
      
      // Apply object-fit to all images
      images.forEach(img => {
        img.style.objectFit = 'cover';
        img.style.position = 'absolute';
        img.style.top = '0';
        img.style.left = '0';
        img.style.width = '100%';
        img.style.height = '100%';
      });
      
      // For adapt ratio, adjust based on actual image dimensions
      if (ratio === 'adapt') {
        images.forEach(img => {
          if (img.complete) {
            setAdaptiveRatio(wrapper, img);
          } else {
            img.addEventListener('load', () => {
              setAdaptiveRatio(wrapper, img);
            });
          }
        });
      }
    }
  });
}

function setAdaptiveRatio(wrapper, img) {
  const naturalWidth = img.naturalWidth;
  const naturalHeight = img.naturalHeight;
  
  if (naturalWidth && naturalHeight) {
    const ratio = (naturalHeight / naturalWidth) * 100;
    wrapper.style.paddingBottom = `${ratio}%`;
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  setTimeout(initProductCardAspectRatio, 300);
  
  // Re-run when sections are loaded dynamically
  document.addEventListener('shopify:section:load', initProductCardAspectRatio);
  document.addEventListener('shopify:section:reload', initProductCardAspectRatio);
});

// Expose for re-initialization
window.initProductCardAspectRatio = initProductCardAspectRatio;